/**
 * useCteReferenceScan — synchronous CTE reference scan for the destructive
 * remove-CTE guard. Reuses the *same* byte walker the `rename-cte` planner
 * ships (`findCteReferences` in `applyStructuralEdit.cte.ts`) so the renderer
 * and the planner agree on "is this CTE referenced downstream" — no second,
 * drifting scanner.
 *
 * PRD: Ralph/VqeUxRefinement/PRD.md — UX-7 ("CTE reference detection is
 * mandatory for destructive remove. No more `references: []` hard-codes.").
 *
 * Returns a stable shape:
 *  - `references` — the array passed straight to `removeCte`'s options.
 *  - `referenceCount` — convenience count for confirm copy.
 *  - `scanFailed` — true when the scan could not be produced (no live buffer
 *    / statement not found). The caller hard-disables remove in that case
 *    (interim safety belt — never silently destructive).
 */
import { useMemo } from 'react';

import { findCteReferences } from '../../../sql-resolver/edit/applyStructuralEdit.cte';
import type { VqeEditContext } from '../edit/editContext';
import type { CteBinding } from '../render/select/extractCtes';

export interface CteReferenceScan {
  readonly references: readonly { range: { startOffset: number; endOffset: number } }[];
  readonly referenceCount: number;
  readonly scanFailed: boolean;
}

const EMPTY_SCAN: CteReferenceScan = {
  references: [],
  referenceCount: 0,
  scanFailed: true,
};

export function useCteReferenceScan(
  binding: CteBinding,
  editContext: VqeEditContext | undefined,
): CteReferenceScan {
  return useMemo(() => {
    if (!editContext) return EMPTY_SCAN;
    const buffer = editContext.service.getParsedBuffer(editContext.documentId);
    if (!buffer) return EMPTY_SCAN;
    const stmt = buffer.documentStructure.statements.find(
      (s) => s.id === editContext.statementId,
    );
    if (!stmt) return EMPTY_SCAN;

    const source = buffer.parsedDocument.source;
    const options = buffer.parsedDocument.options;
    // The name was written quoted iff its first byte is a quote/bracket.
    const firstNameChar = source[binding.nameStart] ?? '';
    const cteNameQuoted =
      firstNameChar === '"' || firstNameChar === '`' || firstNameChar === '[';

    const refs = findCteReferences({
      source,
      options,
      statementEndOffset: stmt.range.endOffset,
      definitionEnd: binding.definitionEnd,
      cteName: binding.name,
      cteNameQuoted,
    });

    return {
      references: refs.map((r) => ({
        range: { startOffset: r.start, endOffset: r.end },
      })),
      referenceCount: refs.length,
      scanFailed: false,
    };
  }, [
    editContext,
    binding.name,
    binding.nameStart,
    binding.definitionEnd,
  ]);
}
